import { useEffect, useRef, useState, type ReactNode } from 'react';
import { resolveItem, type PublishStatus, type SiteConfig } from '@hotpot/core';
import { useT } from '../i18n.js';
import { useMaybeSite } from '../state.js';

export const paths = {
  sites: () => '/',
  site: (id: string) => `/s/${id}`,
  list: (id: string, c: string) => `/s/${id}/c/${c}`,
  entry: (id: string, c: string, slug: string) => `/s/${id}/c/${c}/e/${encodeURIComponent(slug)}`,
  newEntry: (id: string, c: string, from?: string) => `/s/${id}/c/${c}/new${from ? `?from=${encodeURIComponent(from)}` : ''}`,
  file: (id: string, c: string, f: string) => `/s/${id}/f/${c}/${f}`,
  media: (id: string) => `/s/${id}/media`,
  history: (id: string) => `/s/${id}/history`,
  people: (id: string) => `/s/${id}/people`,
};

/** URL and label of a navigation item (`talks` or `settings/home`). */
export function navItem(config: SiteConfig, siteId: string, item: string): { to: string; label: string } | null {
  const r = resolveItem(config, item);
  if (!r) return null;
  if (r.file) return { to: paths.file(siteId, r.collection.name, r.file.name), label: r.file.label };
  return { to: paths.list(siteId, r.collection.name), label: r.collection.label };
}

/** Image from the site repo (or a fresh upload preview). */
export function RepoImage({ path, preview, alt = '', className }: { path?: string; preview?: string; alt?: string; className?: string }) {
  const site = useMaybeSite();
  const [failed, setFailed] = useState(false);
  const src = preview ?? (path && site ? site.handle.storage.mediaUrl(path) : '');
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) return <span className={className} aria-hidden="true" style={{ display: 'block', background: 'var(--placeholder)' }} />;
  return <img className={className} src={src} alt={alt} loading="lazy" decoding="async" onError={() => setFailed(true)} />;
}

export function Status({
  status,
  small,
  url,
  preview,
}: {
  status: PublishStatus | { state: 'live' | 'pending' | 'building' | 'failed' | 'unknown' };
  small?: boolean;
  url?: string;
  /** A preview build (drafts) rather than the live site. */
  preview?: boolean;
}) {
  const { t } = useT();
  const label = preview
    ? { pending: t('previewPending'), building: t('previewPending'), live: t('previewLive'), failed: t('previewFailed'), unknown: t('publishUnknown') }[status.state]
    : {
        pending: t('publishPending'),
        building: t('publishBuilding'),
        live: t('publishLive'),
        failed: t('publishFailed'),
        unknown: t('publishUnknown'),
      }[status.state];
  return (
    <span className={`status ${status.state}${small ? ' small' : ''}${preview ? ' preview' : ''}`}>
      <span className="dot" aria-hidden="true" />
      {status.state === 'live' && url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {label} ↗
        </a>
      ) : (
        label
      )}
      {status.state === 'failed' && 'detailsUrl' in status && status.detailsUrl && (
        <a className="small muted" href={status.detailsUrl} target="_blank" rel="noopener noreferrer">
          {t('buildLog')}
        </a>
      )}
    </span>
  );
}

export function Dialog({ title, children, buttons, onClose, wide }: { title: ReactNode; children?: ReactNode; buttons: ReactNode; onClose: () => void; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('input, select, textarea, button.btn, button');
    first?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [onClose]);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`dialog${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby="dialog-title" ref={ref}>
        <h2 id="dialog-title">{title}</h2>
        {children}
        <div className="buttons">{buttons}</div>
      </div>
    </div>
  );
}

/** `const [confirm, dialog] = useConfirm()` → `if (await confirm({...}))`. */
export function useConfirm(): [(o: { title: string; text?: ReactNode; ok: string; danger?: boolean }) => Promise<boolean>, ReactNode] {
  const { t } = useT();
  const [state, setState] = useState<{ title: string; text?: ReactNode; ok: string; resolve: (v: boolean) => void } | null>(null);
  const ask = (o: { title: string; text?: ReactNode; ok: string }) => new Promise<boolean>((resolve) => setState({ ...o, resolve }));
  const close = (v: boolean) => {
    state?.resolve(v);
    setState(null);
  };
  const dialog = state ? (
    <Dialog
      title={state.title}
      onClose={() => close(false)}
      buttons={
        <>
          <button className="link-btn" onClick={() => close(false)}>
            {t('cancel')}
          </button>
          <button className="btn" onClick={() => close(true)}>
            {state.ok}
          </button>
        </>
      }
    >
      {state.text && <p style={{ margin: 0, lineHeight: 1.35 }}>{state.text}</p>}
    </Dialog>
  ) : null;
  return [ask, dialog];
}

export function Loading() {
  const { t } = useT();
  return <p className="muted" aria-live="polite">{t('loading')}</p>;
}

export function ErrorBox({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  const { t } = useT();
  return (
    <div className="notice pink" role="alert">
      <strong>{t('error')}</strong>
      <div style={{ marginTop: 6 }}>{error.message}</div>
      {onRetry && (
        <button className="link-btn ink" style={{ marginTop: 10 }} onClick={onRetry}>
          {t('retry')}
        </button>
      )}
    </div>
  );
}

export function ChefHat({ size = 120 }: { size?: number }) {
  return <img src={`${import.meta.env.BASE_URL}chef-hat.webp`} alt="" width={size} style={{ height: 'auto' }} />;
}
